/**
 * Net Scan report.
 *
 * The board equivalent of "what is wrong and where": every short (two real
 * nets joined by copper, with the pins involved) and every unrouted ratline
 * (the two pins it spans). Each row jumps the view to it, so a problem you
 * cannot see on a dense board is one click away.
 */

import type { ConnReport, ShortReport, UnroutedReport } from '@sircad/core/net/netlist.ts';
import type { ClearanceViolation } from '@sircad/core/net/clearance.ts';
import { openDialog } from './dialog.ts';

export interface NetReportOptions {
  report: ConnReport;
  /** Clearance rule breaches, measured against the file's own NetScan record. */
  violations?: ClearanceViolation[];
  /** How to render a length in the user's units. */
  formatLength?(inches: number): string;
  /** Jump the view to a point and, for a short, select the offending copper. */
  onLocate(x: number, y: number, entityIds?: number[]): void;
}

/** Long reports are trimmed; the count still tells the truth. */
const MAX_ROWS = 200;

export function showNetReport(opts: NetReportOptions): void {
  const { report } = opts;
  const violations = opts.violations ?? [];

  openDialog({
    title: 'Net Scan',
    build(body, handle) {
      const summary = document.createElement('p');
      summary.className = 'modal-hint';
      const parts: string[] = [];
      if (report.shorts.length) {
        parts.push(`${report.shorts.length} short${report.shorts.length === 1 ? '' : 's'}`);
      }
      if (violations.length) {
        parts.push(`${violations.length} clearance${violations.length === 1 ? '' : 's'}`);
      }
      parts.push(`${report.unrouted.length} unrouted`);
      parts.push(`${report.routed}/${report.total} nets routed`);
      summary.textContent = report.shorts.length || violations.length || report.unrouted.length
        ? parts.join(' · ')
        : `Clean — no shorts, nothing too close, all ${report.total} nets routed.`;
      body.append(summary);

      if (report.shorts.length) {
        body.append(heading('Shorts'));
        for (const s of report.shorts) body.append(shortRow(s, opts));
      }
      if (violations.length) {
        body.append(heading(`Too close (${violations.length})`));
        for (const v of violations.slice(0, MAX_ROWS)) body.append(clearanceRow(v, opts));
        if (violations.length > MAX_ROWS) {
          body.append(note(
            `Showing the ${MAX_ROWS} tightest of ${violations.length}. `
            + 'Fix these and scan again.'));
        }
      }
      if (report.unrouted.length) {
        body.append(heading(`Unrouted (${report.unrouted.length})`));
        for (const u of report.unrouted.slice(0, MAX_ROWS)) body.append(unroutedRow(u, opts));
        if (report.unrouted.length > MAX_ROWS) {
          body.append(note(`Showing ${MAX_ROWS} of ${report.unrouted.length}.`));
        }
      }

      const bar = document.createElement('div');
      bar.className = 'modal-buttons';
      const close = document.createElement('button');
      close.className = 'btn';
      close.textContent = 'Close';
      close.addEventListener('click', () => handle.close());
      bar.append(close);
      body.append(bar);
    },
  });
}

function heading(text: string): HTMLElement {
  const h = document.createElement('div');
  h.className = 'part-group';
  h.textContent = text;
  return h;
}

function note(text: string): HTMLElement {
  const p = document.createElement('p');
  p.className = 'modal-hint';
  p.textContent = text;
  return p;
}

/**
 * One clearance breach: which two nets, how close they got, and what the rule
 * said. Overlapping copper is called out as touching rather than shown as a
 * negative gap, which reads as a typo.
 */
function clearanceRow(v: ClearanceViolation, opts: NetReportOptions): HTMLElement {
  const row = document.createElement('button');
  row.className = 'net-report-row short';
  const nm = document.createElement('span');
  nm.className = 'nr-net';
  nm.textContent = `${v.nets[0]}  ↔  ${v.nets[1]}`;

  const len = opts.formatLength ?? ((n: number) => `${n.toFixed(4)}"`);
  const detail = document.createElement('span');
  detail.className = 'nr-pins';
  const what = v.kind === 'line-line' ? 'track/track'
    : v.kind === 'pad-pad' ? 'pad/pad' : 'track/pad';
  // Name the rule, not just its value. There are three of them, and a row that
  // says only "rule 0.0030" after you have set a clearance to zero reads as
  // stale data rather than as a different rule that was never changed.
  const ruleName = v.kind === 'line-line' ? 'line to line'
    : v.kind === 'pad-pad' ? 'pad to pad' : 'line to pad';
  detail.textContent = v.actual <= 0
    ? `${what} touching — ${ruleName} ${len(v.required)}`
    : `${what} ${len(v.actual)} — ${ruleName} ${len(v.required)}`;

  row.append(nm, detail);
  row.title = 'Locate this clearance breach';
  row.addEventListener('click', () => opts.onLocate(v.at.x, v.at.y, v.entityIds));
  return row;
}

function shortRow(s: ShortReport, opts: NetReportOptions): HTMLElement {
  const row = document.createElement('button');
  row.className = 'net-report-row short';
  const nm = document.createElement('span');
  nm.className = 'nr-net';
  nm.textContent = s.nets.join('  ✕  ');
  const pins = document.createElement('span');
  pins.className = 'nr-pins';
  const named = s.nodes.map((n) => `${n.ref}-${n.pin}`);
  pins.textContent = named.length ? named.join(', ') : `at ${s.at.x.toFixed(3)}, ${s.at.y.toFixed(3)}`;
  row.append(nm, pins);
  row.title = 'Locate this short';
  row.addEventListener('click', () => opts.onLocate(s.at.x, s.at.y, s.entityIds));
  return row;
}

function unroutedRow(u: UnroutedReport, opts: NetReportOptions): HTMLElement {
  const row = document.createElement('button');
  row.className = 'net-report-row';
  const nm = document.createElement('span');
  nm.className = 'nr-net';
  nm.textContent = u.net;
  const pins = document.createElement('span');
  pins.className = 'nr-pins';
  const a = u.from ? `${u.from.ref}-${u.from.pin}` : `${u.x1.toFixed(2)},${u.y1.toFixed(2)}`;
  const b = u.to ? `${u.to.ref}-${u.to.pin}` : `${u.x2.toFixed(2)},${u.y2.toFixed(2)}`;
  pins.textContent = `${a} → ${b}`;
  row.append(nm, pins);
  row.title = 'Locate this ratline';
  row.addEventListener('click', () => opts.onLocate((u.x1 + u.x2) / 2, (u.y1 + u.y2) / 2));
  return row;
}
